import { useState, useCallback, useMemo, useEffect } from "react"
import { debounce } from "lodash"

import PageTitle from "~/library/PageTitle"
import LoadingWrapper from "~/library/LoadingWrapper"
import CatchCard from "~/library/CatchCard"
import NavigationLink from "~/library/NavigationLink"
import { Check, Copy } from "lucide-react"

import getAppConfig from "~/utils/getAppConfig"
import useFetchResource from "~/hooks/useFetchResource"
import { apiAreas } from "~/routes"
import type Area from "~/types/Area"
import { useToast } from "~/utils/Toast"

const AreaList = () => {
  const { venueSlug } = getAppConfig()
  const toast = useToast()
  const endpoint = apiAreas.index.path({ venueSlug })
  const {
    data: areas,
    isLoading,
    refetch,
  } = useFetchResource<{ areas: Area[] }, "areas">(endpoint, "areas", {})

  const [updatingAreas, setUpdatingAreas] = useState<Set<string>>(new Set())
  const [copiedAreaId, setCopiedAreaId] = useState<string | null>(null)

  const updateAreaBookable = useCallback(
    async (areaId: string, currentBookable: boolean) => {
      if (updatingAreas.has(areaId)) {
        return
      }

      setUpdatingAreas((prev) => new Set(prev).add(areaId))

      try {
        await apiAreas.update({
          data: {
            venue_slug: venueSlug,
            id: areaId,
            area: { bookable: !currentBookable },
          },
        })
        await refetch()
      } catch (error) {
        console.error("Failed to update area:", error)
      } finally {
        setUpdatingAreas((prev) => {
          const newSet = new Set(prev)
          newSet.delete(areaId)
          return newSet
        })
      }
    },
    [venueSlug, refetch, updatingAreas]
  )

  // Create debounced version of the update function
  const debouncedToggleBookable = useMemo(
    () => debounce(updateAreaBookable, 300),
    [updateAreaBookable]
  )

  const toggleBookable = useCallback(
    (areaId: string, currentBookable: boolean) => {
      debouncedToggleBookable(areaId, currentBookable)
    },
    [debouncedToggleBookable]
  )

  // Cleanup debounced function on unmount
  useEffect(() => {
    return () => {
      debouncedToggleBookable.cancel()
    }
  }, [debouncedToggleBookable])

  const copyBookingLink = useCallback(
    async (area: Area) => {
      const url = `${window.location.origin}${area.bookingLinkPath}`

      try {
        await navigator.clipboard.writeText(url)
        setCopiedAreaId(area.id.toString())
        toast("Area booking link copied to clipboard!", "success")
        window.setTimeout(() => setCopiedAreaId(null), 1500)
      } catch (error) {
        toast("Failed to copy link", "danger")
      }
    },
    [toast]
  )

  return (
    <LoadingWrapper isLoading={isLoading}>
      <PageTitle title="Bookable Areas" newItemPath="/areas/new" />

      <div className="row">
        {areas.map((area: Area) => {
          return (
            <div className="col-4" key={area.id}>
              <CatchCard>
                <div className="dropdown card-widgets">
                  <a
                    href="#"
                    className="dropdown-toggle arrow-none"
                    data-bs-toggle="dropdown"
                    aria-expanded="false"
                  >
                    <i className="ri-more-fill" />
                  </a>
                </div>
                <div className="d-flex justify-content-between align-items-start mb-2">
                  <div className="d-flex align-items-center gap-2">
                    <h4 className="text-title mt-0 mb-0">
                      <NavigationLink type="areas" to={`/areas/${area.id}`}>
                        {area.name}
                      </NavigationLink>
                    </h4>
                    <button
                      type="button"
                      className="btn btn-light btn-booking-link-copy"
                      title="Copy booking link"
                      data-bs-toggle="tooltip"
                      data-bs-placement="top"
                      data-bs-title="Copy private area booking link"
                      aria-label={`Copy booking link for ${area.name}`}
                      onClick={() => copyBookingLink(area)}
                    >
                      {copiedAreaId === area.id.toString() ? (
                        <Check size={14} />
                      ) : (
                        <Copy size={14} />
                      )}
                    </button>
                  </div>
                  <div className="form-check">
                    <label htmlFor={`bookable-${area.id}`} className="form-check-label small">
                      Bookable
                    </label>
                    <input
                      type="checkbox"
                      className="form-check-input"
                      id={`bookable-${area.id}`}
                      checked={area.bookable}
                      disabled={updatingAreas.has(area.id.toString())}
                      onChange={() => toggleBookable(area.id.toString(), area.bookable)}
                    />
                    {updatingAreas.has(area.id.toString()) && (
                      <small className="text-muted ms-1">
                        <i
                          className="spinner-border spinner-border-sm"
                          role="status"
                          aria-hidden="true"
                        ></i>
                      </small>
                    )}
                  </div>
                </div>
                <div className="d-flex justify-content-between fs-5">
                  <div data-bs-toggle="tooltip" data-bs-placement="top" data-bs-title="Capacity">
                    <i className="uil uil-user" /> Capacity {area.capacity}
                  </div>
                  <div className="ms-2">
                    <i className="uil uil-calender" /> Upcoming Bookings{" "}
                    {area.upcomingBookingsCount}
                  </div>
                </div>
              </CatchCard>
            </div>
          )
        })}
      </div>
    </LoadingWrapper>
  )
}

export default AreaList
